import type { MapId } from "@69ly/maps";
import { planClock, type Combo, type Plan, type PlanAction, type PlanLookup, type PlanPoint, type PlanWarning, type Schedule, type Stuff } from "@69ly/shared";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { beatState, planBeats, type Beat } from "../../lib/beats.ts";
import { useCallouts } from "../../lib/callouts.ts";
import { isPlayer, type PlanActor } from "../../lib/plan.ts";
import { Avatar } from "../Avatar.tsx";
import { ComboGlyphs } from "../ComboBits.tsx";
import { Icon } from "../Icon.tsx";
import { GrenadeIcon } from "../MarkerIcon.tsx";
import { StuffLink } from "../StuffDrawer.tsx";

export interface PartsContext {
  map: MapId;
  plan: Plan;
  schedule: Schedule;
  lookup: PlanLookup;
  stuffs: Stuff[];
  combos: Combo[];
  members: { id: string; name: string; avatarUrl: string | null }[];
}

/** What an action is, in a few words: "Run to B apps", "Bench smoke", "Hold". */
export function ActionText({ a, ctx }: { a: PlanAction; ctx: PartsContext }) {
  const { t } = useTranslation();
  const callouts = useCallouts();
  switch (a.kind) {
    case "move": {
      const place = callouts.at(ctx.map, a.to.x, a.to.y, a.to.z ?? null);
      return <span>{place ? t(`plan.moveTo.${a.pace}`, { place }) : t(`plan.move.${a.pace}`)}</span>;
    }
    case "hold":
      return <span>{t("plan.holdFor", { seconds: a.seconds })}</span>;
    case "call":
      return <span className="plan-call-text">“{a.text}”</span>;
    case "throw": {
      const s = ctx.stuffs.find((x) => x.id === a.stuffId);
      if (!s) return <span className="field-error">{t("plan.missingThrow")}</span>;
      return (
        <span className="plan-line-throw">
          <GrenadeIcon grenade={s.grenade} side={s.side} size={18} />
          {s.title}
        </span>
      );
    }
    case "combo": {
      const c = ctx.combos.find((x) => x.id === a.comboId);
      if (!c) return <span className="field-error">{t("plan.missingCombo")}</span>;
      return (
        <span className="plan-line-throw">
          <ComboGlyphs combo={c} size={9} />
          {c.title}
        </span>
      );
    }
  }
}

/** The way to an action's utility: the single's details, or each throw of a combo. */
export function ActionLinks({ a, ctx }: { a: PlanAction; ctx: PartsContext }) {
  const { t } = useTranslation();
  const ids = a.kind === "throw" ? [a.stuffId] : a.kind === "combo" ? (ctx.combos.find((c) => c.id === a.comboId)?.steps.map((x) => x.stuff.id) ?? []) : [];
  const shown = ids.filter((id) => ctx.stuffs.some((s) => s.id === id) || a.kind === "combo");
  if (shown.length === 0) return null;
  return (
    <span className="plan-line-links">
      {shown.map((id, i) => (
        <StuffLink key={id} id={id} className="plan-line-link">
          <span className="visually-hidden">{t("plan.openUtility", { title: ctx.stuffs.find((s) => s.id === id)?.title ?? ctx.combos.flatMap((c) => c.steps).find((x) => x.stuff.id === id)?.stuff.title ?? "" })}</span>
          {shown.length > 1 ? <span aria-hidden="true">{i + 1}</span> : <Icon name="arrow" size={11} />}
        </StuffLink>
      ))}
    </span>
  );
}

const KIND_ICON = { move: "route", hold: "hold", throw: "target", combo: "target", call: "chat" } as const;
export const actionIcon = (a: PlanAction) => KIND_ICON[a.kind];

/** A warning about an action, in words. */
export function warningText(w: PlanWarning, t: (k: string, o?: Record<string, unknown>) => string) {
  if (w.kind === "too_early") return t("plan.warn.tooEarly", { time: planClock(w.earliest ?? 0) });
  return t(`plan.warn.${w.kind}`);
}

/** The header of a player: number, role, who plays it, the weapon. */
export function ActorHead({ a, ctx }: { a: PlanActor; ctx: PartsContext }) {
  const { t } = useTranslation();
  if (!isPlayer(a))
    return (
      <span className="plan-actor-head">
        <span className="pp-dot is-enemy">E</span>
        <b>{a.label || t("plan.opponent")}</b>
        <span className="muted small">{t(`plan.weapon.${a.weapon}`)}</span>
      </span>
    );
  const m = ctx.members.find((x) => x.id === a.memberId);
  return (
    <span className="plan-actor-head">
      <span className={`pp-dot slot-${a.slot}`}>{a.slot}</span>
      <b>{t(`role.${a.role}`)}</b>
      {m ? (
        <span className="plan-member small">
          <Avatar name={m.name} url={m.avatarUrl} size={18} />
          {m.name}
        </span>
      ) : (
        <span className="muted small">{t("plan.unpinned")}</span>
      )}
      <span className="muted small plan-weapon">{t(`plan.weapon.${a.weapon}`)}</span>
    </span>
  );
}

/** A player's beats, worked out again when the plan changes; stops named by their callout. */
export function useBeats(actor: PlanActor | null, ctx: PartsContext): Beat[] {
  const callouts = useCallouts();
  return useMemo(
    () => (actor ? planBeats(actor, ctx.schedule, (p: PlanPoint) => callouts.at(ctx.map, p.x, p.y, p.z ?? null)) : []),
    [actor, ctx.schedule, ctx.map, callouts],
  );
}

/** "Palace Alley › T Ramp (walk)": a route's stops, its pace where it is not a run. */
export function RouteStops({ beat }: { beat: Extract<Beat, { kind: "route" }> }) {
  const { t } = useTranslation();
  return (
    <span className="beat-stops">
      {beat.stops.map((s, i) => {
        const paces = [...new Set(s.paces.filter((p) => p !== "run"))];
        return (
          <span key={i} className="beat-stop-wrap">
            {i > 0 && (
              <span className="beat-sep" aria-hidden="true">
                ›
              </span>
            )}
            <span className="beat-stop">
              {s.name ?? t("plan.somewhere")}
              {paces.length > 0 && <span className="beat-pace">{paces.map((p) => t(`plan.paces.${p}`).toLowerCase()).join(", ")}</span>}
            </span>
          </span>
        );
      })}
    </span>
  );
}

/** Seconds as "7 s". */
const secs = (s: number) => `${Math.max(1, Math.round(s))} s`;

/** What a beat says: the route and its stops, the throw and where it lands, the hold and what it faces, the call. */
export function BeatBody({ beat, ctx }: { beat: Beat; ctx: PartsContext }) {
  const { t } = useTranslation();
  const callouts = useCallouts();
  const clock = (e: number | null) => (e === null ? "–" : planClock(ctx.schedule.toClock(e)));
  if (beat.kind === "route")
    return (
      <span className="beat-body">
        <span className="beat-label">{t("plan.route")}</span>
        <RouteStops beat={beat} />
        {beat.start !== null && beat.end !== null && <span className="beat-meta">{secs(beat.end - beat.start)}</span>}
      </span>
    );
  const a = beat.action;
  switch (a.kind) {
    case "throw": {
      const s = ctx.stuffs.find((x) => x.id === a.stuffId);
      if (!s) return <span className="beat-body field-error">{t("plan.missingThrow")}</span>;
      const lands = callouts.at(ctx.map, s.landing.x, s.landing.y, s.landing.z ?? s.throw.z);
      return (
        <span className="beat-body">
          <GrenadeIcon grenade={s.grenade} side={s.side} size={22} />
          <b className="beat-title">{s.title}</b>
          <span className="beat-meta">{lands ? t("plan.landsAt", { place: lands, time: clock(beat.lands) }) : t("plan.landsWhen", { time: clock(beat.lands) })}</span>
        </span>
      );
    }
    case "combo": {
      const c = ctx.combos.find((x) => x.id === a.comboId);
      if (!c) return <span className="beat-body field-error">{t("plan.missingCombo")}</span>;
      return (
        <span className="beat-body">
          <ComboGlyphs combo={c} size={9} />
          <b className="beat-title">{c.title}</b>
          <span className="beat-meta">{t("plan.comboLands", { count: c.steps.length, time: clock(beat.lands) })}</span>
        </span>
      );
    }
    case "hold":
      return (
        <span className="beat-body">
          <span className="beat-label">{t("plan.holdFor", { seconds: a.seconds })}</span>
          {beat.facing && <span className="beat-meta">{t("plan.facing", { place: beat.facing })}</span>}
        </span>
      );
    case "call":
      return (
        <span className="beat-body">
          <span className="beat-call">“{a.text}”</span>
        </span>
      );
    default:
      return null;
  }
}

const BEAT_ICON = { route: "route", hold: "hold", call: "chat", throw: "target", combo: "target", move: "route" } as const;
export const beatIcon = (b: Beat) => (b.kind === "route" ? BEAT_ICON.route : BEAT_ICON[b.action.kind]);
const beatType = (b: Beat) => (b.kind === "route" ? "route" : b.action.kind);

/** The utility a player throws, as small drawings: a summary at a glance. */
function Kit({ actor, ctx }: { actor: PlanActor; ctx: PartsContext }) {
  const marks = (actor.actions as PlanAction[]).flatMap((a) => {
    if (a.kind === "throw") {
      const s = ctx.stuffs.find((x) => x.id === a.stuffId);
      return s ? [{ key: a.id, grenade: s.grenade, side: s.side, title: s.title }] : [];
    }
    if (a.kind === "combo") {
      const c = ctx.combos.find((x) => x.id === a.comboId);
      return c ? c.steps.map((st, i) => ({ key: `${a.id}-${i}`, grenade: st.stuff.grenade, side: c.side, title: `${c.title} · ${st.stuff.title}` })) : [];
    }
    return [];
  });
  if (marks.length === 0) return null;
  return (
    <span className="part-kit" aria-hidden="true">
      {marks.slice(0, 6).map((m) => (
        <GrenadeIcon key={m.key} grenade={m.grenade} side={m.side} size={20} title={m.title} />
      ))}
      {marks.length > 6 && <span className="part-kit-more">+{marks.length - 6}</span>}
    </span>
  );
}

/** One player: a summary line that opens on their beats, on a time rail. */
function PartCard({
  actor,
  ctx,
  now,
  open,
  onToggle,
  onSeek,
  meId,
}: {
  actor: PlanActor;
  ctx: PartsContext;
  now: number;
  open: boolean;
  onToggle: (() => void) | null;
  onSeek: (elapsed: number) => void;
  meId: string | null;
}) {
  const { t } = useTranslation();
  const beats = useBeats(actor, ctx);
  const clock = (e: number | null) => (e === null ? "–" : planClock(ctx.schedule.toClock(e)));
  const first = beats.find((b) => b.start !== null)?.start ?? null;
  const last = [...beats].reverse().find((b) => b.end !== null)?.end ?? null;
  const active = beats.some((b) => beatState(b, now) === "is-now");
  const mine = isPlayer(actor) && !!actor.memberId && actor.memberId === meId;
  const head = (
    <>
      <ActorHead a={actor} ctx={ctx} />
      {mine && <span className="part-you">{t("live.you")}</span>}
      <Kit actor={actor} ctx={ctx} />
      <span className="part-span small">
        {beats.length === 0 ? t("plan.holdsStartShort") : `${t("plan.beats", { count: beats.length })} · ${clock(first)} → ${clock(last)}`}
      </span>
      {onToggle && <Icon name="chevronDown" size={14} />}
    </>
  );
  return (
    <li className={`part${isPlayer(actor) ? ` slot-${actor.slot}` : " is-enemy"}${open ? " is-open" : ""}${active ? " is-active" : ""}`}>
      {onToggle ? (
        <button type="button" className="part-head" aria-expanded={open} onClick={onToggle}>
          {head}
        </button>
      ) : (
        <div className="part-head">{head}</div>
      )}
      {open &&
        (beats.length === 0 ? (
          <p className="muted small part-empty">{t("plan.holdsStart")}</p>
        ) : (
          <ol className="beats">
            {beats.map((b) => {
              const state = beatState(b, now);
              const utility = b.kind === "action" && (b.action.kind === "throw" || b.action.kind === "combo");
              return (
                <li key={b.key} className={`beat kind-${beatType(b)}${state ? ` ${state}` : ""}${utility ? " is-utility" : ""}${b.warnings.length ? " has-warning" : ""}`}>
                  <span className="beat-dot" aria-hidden="true" />
                  <div className="beat-row">
                    {/* The page's clock moves in quarter seconds: land on the beat, not just before it. */}
                    <button type="button" className="beat-main" onClick={() => b.start !== null && onSeek(Math.ceil(b.start * 4) / 4)} title={t("plan.jumpTo")}>
                      <span className="beat-time mono">{clock(b.start)}</span>
                      <Icon name={beatIcon(b)} size={14} />
                      <BeatBody beat={b} ctx={ctx} />
                      {b.kind === "route" && b.linked && <Icon name="link" size={12} />}
                      {b.kind === "action" && b.action.after && <Icon name="link" size={12} />}
                    </button>
                    {b.kind === "action" && <ActionLinks a={b.action} ctx={ctx} />}
                  </div>
                  {b.warnings.map((w, i) => (
                    <p key={i} className="plan-warn small">
                      {warningText(w, t)}
                    </p>
                  ))}
                </li>
              );
            })}
          </ol>
        ))}
    </li>
  );
}

/**
 * Who does what: each player as a line (their role, who plays it, the utility
 * they throw, how long their part lasts) that opens on their beats: moves in a
 * row are one route, then each throw (and where it lands), hold (and what it
 * faces) and call, on a time rail. The beat happening now lights up as the plan
 * plays; a click on one jumps there. Everyone: one open at a time (mine first);
 * a player picked: theirs alone.
 */
export const PlanParts = memo(function PlanParts({
  ctx,
  now,
  focus,
  onFocus,
  meId,
  onSeek,
}: {
  ctx: PartsContext;
  /** The playhead, rounded: the list does not need every frame. */
  now: number;
  focus: string | null;
  onFocus: (id: string | null) => void;
  meId: string | null;
  onSeek: (elapsed: number) => void;
}) {
  const { t } = useTranslation();
  const { plan } = ctx;
  const mine = plan.players.find((p) => p.memberId && p.memberId === meId) ?? null;
  // Everyone: which one is open ("" for none); mine, else the first, to begin with.
  const [open, setOpen] = useState<string | null>(null);
  const openId = focus ?? (open === null ? (mine?.id ?? plan.players[0]?.id ?? null) : open);
  const shown: PlanActor[] = focus ? [...plan.players, ...plan.opponents].filter((a) => a.id === focus) : [...plan.players, ...plan.opponents];
  return (
    <div className="plan-parts">
      <div className="plan-focus" role="group" aria-label={t("plan.focus")}>
        <button type="button" className="chip" aria-pressed={focus === null} onClick={() => onFocus(null)}>
          {t("plan.everyone")}
        </button>
        {mine && (
          <button type="button" className="chip" aria-pressed={focus === mine.id} onClick={() => onFocus(mine.id)}>
            <Icon name="user" size={12} />
            {t("plan.myPart")}
          </button>
        )}
        {plan.players.map((p) => (
          <button key={p.id} type="button" className="chip" aria-pressed={focus === p.id} onClick={() => onFocus(focus === p.id ? null : p.id)} title={t(`role.${p.role}`)}>
            <span className={`pp-dot slot-${p.slot}`}>{p.slot}</span>
          </button>
        ))}
      </div>
      {plan.players.length === 0 && <p className="muted small">{t("plan.noPlayers")}</p>}
      <ol className="part-list">
        {shown.map((a) => (
          <PartCard
            key={a.id}
            actor={a}
            ctx={ctx}
            now={now}
            meId={meId}
            open={a.id === openId}
            onToggle={focus ? null : () => setOpen(openId === a.id ? "" : a.id)}
            onSeek={onSeek}
          />
        ))}
      </ol>
    </div>
  );
});
